import { useState, useEffect, useRef } from "react";

// ── CONFIG ────────────────────────────────────────────────────
const SUPABASE_URL      = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;
const CLOUDINARY_CLOUD  = process.env.REACT_APP_CLOUDINARY_CLOUD;
const CLOUDINARY_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET;
const SITE_URL          = process.env.REACT_APP_SITE_URL || window.location.origin;

// ── API HELPERS ───────────────────────────────────────────────
async function sbGet(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  if (!r.ok) return null;
  const d = await r.json();
  return Array.isArray(d) ? d[0] || null : d;
}

async function sbGetAll(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  if (!r.ok) return [];
  const d = await r.json();
  return Array.isArray(d) ? d : [];
}

async function sbPost(path, body) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) { console.error("[sbPost]", await r.text()); return null; }
  const d = await r.json();
  return Array.isArray(d) ? d[0] : d;
}

async function sbPatch(path, body) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "PATCH",
    headers: {
      apikey: SUPABASE_ANON_KEY,
      Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=representation",
    },
    body: JSON.stringify(body),
  });
  if (!r.ok) { console.error("[sbPatch]", await r.text()); return null; }
  const d = await r.json();
  return Array.isArray(d) ? d[0] : d;
}

async function sbDelete(path) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/${path}`, {
    method: "DELETE",
    headers: { apikey: SUPABASE_ANON_KEY, Authorization: `Bearer ${SUPABASE_ANON_KEY}` },
  });
  return r.ok;
}

async function uploadToCloudinary(file, folder = "mykisah/gallery") {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("upload_preset", CLOUDINARY_PRESET);
  fd.append("folder", folder);
  const r = await fetch(`https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`, {
    method: "POST", body: fd,
  });
  if (!r.ok) return null;
  const d = await r.json();
  return d.secure_url || null;
}

function generateToken() {
  return Math.random().toString(36).slice(2, 10) +
         Math.random().toString(36).slice(2, 6);
}

function fmt(n) { return "Rp " + n.toLocaleString("id-ID"); }

// ── STATUS META ───────────────────────────────────────────────
const STATUS = {
  pending_payment:    { label:"Menunggu Pembayaran",       color:"#C9A84C",              icon:"⏳", desc:"Order sudah masuk. Silakan transfer dan upload bukti bayar." },
  payment_uploaded:   { label:"Bukti Bayar Diterima",      color:"#7A9E7E",              icon:"📎", desc:"Bukti transfer sudah kami terima. Tim sedang memverifikasi, biasanya 1–3 jam kerja." },
  payment_confirmed:  { label:"Pembayaran Dikonfirmasi",   color:"#7A9E7E",              icon:"✅", desc:"Pembayaran dikonfirmasi. Tim sedang mengerjakan undanganmu." },
  in_progress:        { label:"Sedang Dikerjakan",         color:"#9B87B8",              icon:"🎨", desc:"Undanganmu sedang kami buat. Selesai dalam 1×24 jam kerja." },
  active:             { label:"Undangan Aktif",            color:"#C9A84C",              icon:"✦", desc:"Undanganmu sudah siap! Bagikan link dan kelola konten di bawah." },
  expired:            { label:"Masa Aktif Habis",          color:"rgba(240,232,216,.28)",icon:"⌛", desc:"Masa aktif habis. Hubungi kami untuk perpanjangan." },
  revision:           { label:"Perlu Revisi",              color:"#C97B6F",              icon:"📝", desc:"Ada catatan revisi. Cek WhatsApp kamu." },
};

const STEPS = [
  { key:"pending_payment",   label:"Order masuk" },
  { key:"payment_uploaded",  label:"Bukti bayar dikirim" },
  { key:"payment_confirmed", label:"Pembayaran dikonfirmasi" },
  { key:"in_progress",       label:"Undangan dibuat" },
  { key:"active",            label:"Undangan aktif" },
];
const STEP_ORDER = STEPS.map(s=>s.key);
function getStepState(stepKey, currentStatus) {
  const si = STEP_ORDER.indexOf(stepKey);
  const ci = STEP_ORDER.indexOf(currentStatus);
  if (si < ci)  return "done";
  if (si === ci) return "current";
  return "pending";
}

const ADDON_LABELS = {
  link_personal:"🔗 Link Personal", pantau_rsvp:"📊 Pantau RSVP",
  notif_email:"📧 Notif Email",     galeri_foto:"🖼️ Galeri Foto",
  amplop_digital:"💌 Amplop",       live_streaming:"📺 Live Streaming",
  musik_latar:"🎵 Musik Latar",     custom_subdomain:"🌐 Subdomain",  selamanya:"♾️ Selamanya",
  qr_checkin:"✅ QR Check-in",      export_excel:"📥 Export Excel",
};

// ── MAX FOTO (bisa dikembangkan dengan add-on tambahan nanti) ─
const PHOTO_LIMIT = 8;

// ══════════════════════════════════════════════════════════════
// CSS
// ══════════════════════════════════════════════════════════════
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500&family=Cormorant+Garamond:ital,wght@0,300;1,400&family=Josefin+Sans:wght@200;300;400&display=swap');
  *, *::before, *::after { box-sizing:border-box; margin:0; padding:0; }
  body { background:#0C0B09; color:rgba(240,232,216,.9); font-family:'Josefin Sans',sans-serif; font-weight:300; min-height:100vh; }

  @keyframes ckFadeUp { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
  @keyframes ckSpin   { to{transform:rotate(360deg)} }

  .ck-page { min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:60px 24px; }

  /* ── SEARCH ── */
  .ck-box   { width:100%; max-width:500px; }
  .ck-tag   { font-family:'Cinzel',serif; font-size:12px; letter-spacing:.54em; color:rgba(201,168,76,.5); text-align:center; margin-bottom:12px; }
  .ck-title { font-family:'Cinzel',serif; font-size:clamp(1.8rem,5vw,2.8rem); letter-spacing:.04em; text-align:center; margin-bottom:8px; }
  .ck-sub   { font-family:'Cormorant Garamond',serif; font-style:italic; font-size:1.05rem; color:rgba(240,232,216,.3); text-align:center; margin-bottom:40px; }
  .ck-input-row { display:flex; border:1px solid rgba(201,168,76,.22); }
  .ck-inp   { flex:1; padding:15px 18px; background:rgba(255,255,255,.025); border:none; color:rgba(240,232,216,.9); font-family:'Josefin Sans',sans-serif; font-size:16px; font-weight:300; outline:none; letter-spacing:.06em; }
  .ck-inp::placeholder { color:rgba(240,232,216,.22); letter-spacing:.1em; font-size:16px; }
  .ck-btn  { padding:15px 24px; background:#C9A84C; color:#0C0B09; font-family:'Cinzel',serif; font-size:13px; letter-spacing:.2em; border:none; cursor:pointer; transition:opacity .2s; white-space:nowrap; }
  .ck-btn:hover { opacity:.88; } .ck-btn:disabled { opacity:.4; cursor:default; }
  .ck-err  { font-size:12px; letter-spacing:.1em; color:rgba(201,123,111,.7); text-align:center; margin-top:16px; }
  .ck-spin { width:20px; height:20px; border:1px solid rgba(201,168,76,.25); border-top-color:#C9A84C; border-radius:50%; animation:ckSpin 1s linear infinite; margin:24px auto; }

  /* ── CARD ── */
  .ck-card  { width:100%; max-width:620px; border:1px solid rgba(201,168,76,.14); background:#0E0C0A; animation:ckFadeUp .7s ease both; margin-top:0; box-shadow:0 24px 80px rgba(0,0,0,.4); }

  .ck-card-top { padding:24px 24px 20px; border-bottom:1px solid rgba(201,168,76,.08); display:flex; justify-content:space-between; align-items:flex-start; gap:14px; }
  .ck-order-id { font-family:'Cinzel',serif; font-size:14px; letter-spacing:.26em; color:rgba(201,168,76,.6); margin-bottom:6px; }
  .ck-couple   { font-family:'Cinzel',serif; font-size:clamp(1.2rem,4vw,1.7rem); letter-spacing:.04em; line-height:1.1; }
  .ck-bride    { color:#C9A84C; font-style:italic; font-family:'Cormorant Garamond',serif; }
  .ck-status-badge { display:flex; flex-direction:column; align-items:flex-end; gap:5px; flex-shrink:0; }
  .ck-status-icon  { font-size:1.6rem; }
  .ck-status-label { font-family:'Cinzel',serif; font-size:12px; letter-spacing:.18em; white-space:nowrap; }

  /* detail rows */
  .ck-details { padding:20px 24px; border-bottom:1px solid rgba(201,168,76,.07); display:grid; grid-template-columns:1fr 1fr; gap:14px; }
  .ck-det-key { font-size:11px; letter-spacing:.28em; color:rgba(240,232,216,.3); margin-bottom:4px; }
  .ck-det-val { font-size:13px; letter-spacing:.1em; color:rgba(240,232,216,.75); }

  /* addons chips */
  .ck-addons { padding:18px 24px; border-bottom:1px solid rgba(201,168,76,.07); }
  .ck-addons-title { font-family:'Cinzel',serif; font-size:12px; letter-spacing:.34em; color:rgba(201,168,76,.44); margin-bottom:11px; }
  .ck-chips { display:flex; flex-wrap:wrap; gap:5px; }
  .ck-chip  { font-size:11px; letter-spacing:.08em; padding:3px 8px; background:rgba(201,168,76,.06); border:1px solid rgba(201,168,76,.15); color:rgba(240,232,216,.5); }

  /* status info + timeline */
  .ck-status-info { padding:18px 24px; border-bottom:1px solid rgba(201,168,76,.07); }
  .ck-status-desc { font-size:13px; letter-spacing:.1em; color:rgba(240,232,216,.45); line-height:1.85; }
  .ck-timeline    { padding:18px 24px; border-bottom:1px solid rgba(201,168,76,.07); }
  .ck-tl-title    { font-family:'Cinzel',serif; font-size:12px; letter-spacing:.34em; color:rgba(201,168,76,.44); margin-bottom:14px; }
  .ck-tl-row      { display:flex; gap:12px; align-items:center; margin-bottom:10px; }
  .ck-tl-row:last-child { margin-bottom:0; }
  .ck-tl-dot      { width:8px; height:8px; border-radius:50%; flex-shrink:0; }
  .ck-tl-dot.done    { background:rgba(201,168,76,.8); }
  .ck-tl-dot.current { background:#C9A84C; box-shadow:0 0 0 3px rgba(201,168,76,.18); }
  .ck-tl-dot.pending { background:rgba(255,255,255,.1); }
  .ck-tl-label       { font-size:12px; letter-spacing:.1em; color:rgba(240,232,216,.4); }
  .ck-tl-label.done    { color:rgba(240,232,216,.65); }
  .ck-tl-label.current { color:rgba(240,232,216,.9); }

  /* ── LINK BOX ── */
  .ck-link-box   { padding:20px 24px; background:rgba(201,168,76,.03); border-bottom:1px solid rgba(201,168,76,.1); }
  .ck-section-title { font-family:'Cinzel',serif; font-size:13px; letter-spacing:.34em; color:rgba(201,168,76,.65); margin-bottom:13px; }
  .ck-link-row   { display:flex; border:1px solid rgba(201,168,76,.2); margin-bottom:8px; }
  .ck-link-url   { flex:1; padding:10px 12px; background:transparent; border:none; color:rgba(240,232,216,.65); font-family:'Josefin Sans',sans-serif; font-size:17px; font-weight:300; outline:none; letter-spacing:.04em; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:text; }
  .ck-link-copy  { padding:10px 16px; background:rgba(201,168,76,.12); border:none; border-left:1px solid rgba(201,168,76,.2); color:rgba(201,168,76,.8); font-family:'Cinzel',serif; font-size:12px; letter-spacing:.18em; cursor:pointer; transition:all .2s; white-space:nowrap; }
  .ck-link-copy:hover { background:rgba(201,168,76,.22); }
  .ck-link-open  { display:inline-flex; align-items:center; gap:7px; font-family:'Cinzel',serif; font-size:13px; letter-spacing:.2em; color:#C9A84C; text-decoration:none; transition:opacity .2s; }
  .ck-link-open:hover { opacity:.75; }

  /* ── GALERI FOTO ── */
  .ck-gallery-box { padding:22px 24px; border-bottom:1px solid rgba(201,168,76,.08); }
  .ck-gallery-meta { font-size:12px; letter-spacing:.12em; color:rgba(240,232,216,.3); margin-bottom:14px; }
  .ck-gallery-meta span { color:rgba(201,168,76,.6); }
  .ck-photo-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:6px; }
  .ck-photo-slot { aspect-ratio:4/3; position:relative; overflow:hidden; border:1px solid rgba(201,168,76,.1); background:rgba(255,255,255,.02); cursor:pointer; transition:border-color .2s; }
  .ck-photo-slot:hover { border-color:rgba(201,168,76,.35); }
  .ck-photo-slot img { width:100%; height:100%; object-fit:cover; display:block; }
  .ck-photo-slot.empty { display:flex; flex-direction:column; align-items:center; justify-content:center; gap:5px; }
  .ck-photo-slot.empty .plus { font-size:1.3rem; color:rgba(201,168,76,.28); }
  .ck-photo-slot.empty .lbl { font-family:'Cinzel',serif; font-size:10px; letter-spacing:.2em; color:rgba(201,168,76,.28); }
  .ck-photo-slot.uploading { opacity:.5; pointer-events:none; }
  .ck-photo-remove { position:absolute; top:4px; right:4px; width:20px; height:20px; background:rgba(0,0,0,.7); border:none; border-radius:50%; color:rgba(240,232,216,.7); font-size:14px; cursor:pointer; display:flex; align-items:center; justify-content:center; line-height:1; transition:background .2s; }
  .ck-photo-remove:hover { background:rgba(201,123,111,.8); }
  .ck-photo-saving { margin-top:10px; font-size:12px; letter-spacing:.14em; color:rgba(201,168,76,.5); text-align:center; }

  /* ── TAMU (LINK PERSONAL) ── */
  .ck-guests-box { padding:22px 24px; border-bottom:1px solid rgba(201,168,76,.08); }
  .ck-guests-meta { font-size:12px; letter-spacing:.12em; color:rgba(240,232,216,.3); margin-bottom:14px; }
  .ck-guests-meta span { color:rgba(201,168,76,.6); }

  /* add guest form */
  .ck-guest-form { display:flex; gap:6px; margin-bottom:14px; flex-wrap:wrap; }
  .ck-guest-inp  { flex:1; min-width:120px; padding:9px 12px; background:rgba(255,255,255,.02); border:1px solid rgba(201,168,76,.14); color:rgba(240,232,216,.85); font-family:'Josefin Sans',sans-serif; font-size:15px; font-weight:300; outline:none; transition:border-color .2s; }
  .ck-guest-inp:focus { border-color:rgba(201,168,76,.38); }
  .ck-guest-inp::placeholder { color:rgba(240,232,216,.18); }
  .ck-guest-add  { padding:9px 16px; background:#C9A84C; color:#0C0B09; font-family:'Cinzel',serif; font-size:12px; letter-spacing:.2em; border:none; cursor:pointer; transition:opacity .2s; white-space:nowrap; }
  .ck-guest-add:hover { opacity:.85; } .ck-guest-add:disabled { opacity:.35; cursor:default; }

  /* guest list */
  .ck-guest-list { display:flex; flex-direction:column; gap:7px; }
  .ck-guest-row  { border:1px solid rgba(201,168,76,.09); padding:12px 14px; display:flex; align-items:center; gap:10px; background:rgba(255,255,255,.01); }
  .ck-guest-name { font-size:13px; letter-spacing:.08em; color:rgba(240,232,216,.8); flex-shrink:0; min-width:90px; }
  .ck-guest-phone { font-size:12px; letter-spacing:.06em; color:rgba(240,232,216,.3); flex-shrink:0; }
  .ck-guest-link-wrap { flex:1; display:flex; border:1px solid rgba(201,168,76,.12); overflow:hidden; min-width:0; }
  .ck-guest-link-url  { flex:1; padding:6px 10px; background:transparent; border:none; color:rgba(240,232,216,.4); font-family:'Josefin Sans',sans-serif; font-size:16px; font-weight:300; outline:none; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; cursor:text; min-width:0; }
  .ck-guest-link-copy { padding:6px 12px; background:rgba(201,168,76,.08); border:none; border-left:1px solid rgba(201,168,76,.12); color:rgba(201,168,76,.65); font-family:'Cinzel',serif; font-size:11px; letter-spacing:.14em; cursor:pointer; transition:all .2s; white-space:nowrap; flex-shrink:0; }
  .ck-guest-link-copy:hover { background:rgba(201,168,76,.18); }
  .ck-guest-del  { padding:5px 9px; background:transparent; border:1px solid rgba(201,123,111,.2); color:rgba(201,123,111,.5); font-size:16px; cursor:pointer; transition:all .2s; flex-shrink:0; border-radius:2px; }
  .ck-guest-del:hover { background:rgba(201,123,111,.08); color:rgba(201,123,111,.8); border-color:rgba(201,123,111,.4); }
  .ck-guest-empty { padding:22px; text-align:center; font-size:12px; letter-spacing:.14em; color:rgba(240,232,216,.2); border:1px dashed rgba(201,168,76,.1); }

  /* copy all wa */
  .ck-copy-all { margin-top:12px; display:flex; gap:8px; flex-wrap:wrap; }
  .ck-btn-ghost { padding:8px 16px; background:transparent; border:1px solid rgba(201,168,76,.18); color:rgba(201,168,76,.65); font-family:'Cinzel',serif; font-size:12px; letter-spacing:.18em; cursor:pointer; transition:all .2s; white-space:nowrap; }
  .ck-btn-ghost:hover { background:rgba(201,168,76,.06); border-color:rgba(201,168,76,.38); }

  /* ── FOOTER ── */
  .ck-card-foot { padding:16px 24px; display:flex; justify-content:space-between; align-items:center; flex-wrap:wrap; gap:10px; }
  .ck-foot-total { font-family:'Cinzel',serif; font-size:13px; letter-spacing:.18em; color:rgba(201,168,76,.6); }
  .ck-new-search { padding:8px 18px; background:transparent; border:1px solid rgba(201,168,76,.18); color:rgba(201,168,76,.6); font-family:'Cinzel',serif; font-size:12px; letter-spacing:.18em; cursor:pointer; transition:all .2s; }
  .ck-new-search:hover { border-color:rgba(201,168,76,.38); color:rgba(201,168,76,.88); }

  /* toast */
  .ck-toast { position:fixed; bottom:24px; left:50%; transform:translateX(-50%); background:#1A1610; border:1px solid rgba(201,168,76,.3); padding:10px 22px; font-family:'Cinzel',serif; font-size:13px; letter-spacing:.18em; color:rgba(201,168,76,.8); z-index:200; animation:ckFadeUp .3s ease both; white-space:nowrap; pointer-events:none; }

  @media(max-width:520px) {
    .ck-details { grid-template-columns:1fr; }
    .ck-card-top { flex-direction:column-reverse; }
    .ck-status-badge { flex-direction:row; align-items:center; }
    .ck-photo-grid { grid-template-columns:repeat(2,1fr); }
    .ck-guest-row { flex-wrap:wrap; }
  }
`;

// ══════════════════════════════════════════════════════════════
// SECTION: GALERI FOTO
// ══════════════════════════════════════════════════════════════
function GallerySection({ wedding, onSaved }) {
  const [photos,    setPhotos]    = useState(wedding.content?.gallery || []);
  const [uploading, setUploading] = useState(null); // index slot yang sedang upload
  const [saving,    setSaving]    = useState(false);
  const fileRef = useRef();
  const pendingSlot = useRef(null);

  const MAX = PHOTO_LIMIT;

  async function handleFile(file) {
    if (!file || !file.type.startsWith("image/")) return;
    const slot = pendingSlot.current;
    setUploading(slot);
    const url = await uploadToCloudinary(file, `mykisah/gallery/${wedding.id}`);
    if (!url) { setUploading(null); return; }

    const next = [...photos];
    if (slot < next.length) {
      next[slot] = url;          // replace existing
    } else {
      next.push(url);            // add new
    }
    setPhotos(next);
    setUploading(null);

    // Langsung simpan ke Supabase
    setSaving(true);
    await sbPatch(`weddings?id=eq.${wedding.id}`, {
      content: { ...wedding.content, gallery: next },
    });
    setSaving(false);
    onSaved(next);
  }

  async function removePhoto(idx) {
    const next = photos.filter((_, i) => i !== idx);
    setPhotos(next);
    setSaving(true);
    await sbPatch(`weddings?id=eq.${wedding.id}`, {
      content: { ...wedding.content, gallery: next },
    });
    setSaving(false);
    onSaved(next);
  }

  // Buat array slot: isi foto yg ada + slot kosong sampai MAX
  const slots = [
    ...photos.map((url, i) => ({ type:"photo", url, idx: i })),
    ...(photos.length < MAX
      ? Array.from({ length: MAX - photos.length }, (_, i) => ({ type:"empty", idx: photos.length + i }))
      : []),
  ];

  return (
    <div className="ck-gallery-box">
      <div className="ck-section-title">🖼️ GALERI FOTO</div>
      <div className="ck-gallery-meta">
        Foto tersimpan: <span>{photos.length}/{MAX}</span>
        {" · "}Klik slot untuk upload atau ganti foto
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        style={{ display:"none" }}
        onChange={e => handleFile(e.target.files[0])}
      />

      <div className="ck-photo-grid">
        {slots.map(slot => (
          slot.type === "photo"
            ? (
              <div key={slot.idx} className={`ck-photo-slot${uploading===slot.idx?" uploading":""}`}>
                <img src={slot.url} alt={`Foto ${slot.idx+1}`}
                  onClick={() => { pendingSlot.current = slot.idx; fileRef.current.click(); }}/>
                <button className="ck-photo-remove"
                  onClick={e => { e.stopPropagation(); removePhoto(slot.idx); }}>✕</button>
              </div>
            ) : (
              <div key={slot.idx}
                className={`ck-photo-slot empty${uploading===slot.idx?" uploading":""}`}
                onClick={() => { pendingSlot.current = slot.idx; fileRef.current.click(); }}>
                {uploading === slot.idx
                  ? <div className="ck-spin"/>
                  : <>
                      <span className="plus">＋</span>
                      <span className="lbl">FOTO {slot.idx+1}</span>
                    </>
                }
              </div>
            )
        ))}
      </div>

      {saving && <div className="ck-photo-saving">Menyimpan…</div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// SECTION: LINK PERSONAL (MANAJEMEN TAMU)
// ══════════════════════════════════════════════════════════════
function GuestsSection({ wedding, invitationUrl }) {
  const [guests,   setGuests]   = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [name,     setName]     = useState("");
  const [phone,    setPhone]    = useState("");
  const [adding,   setAdding]   = useState(false);
  const [copied,   setCopied]   = useState("");

  useEffect(() => {
    async function load() {
      const list = await sbGetAll(`guests?wedding_id=eq.${wedding.id}&order=created_at.asc`);
      setGuests(list);
      setLoading(false);
    }
    load();
  }, [wedding.id]);

  function guestUrl(token) {
    return `${SITE_URL}/undangan/${wedding.slug}?token=${token}`;
  }

  async function addGuest() {
    if (!name.trim() || adding) return;
    setAdding(true);
    const token = generateToken();
    const g = await sbPost("guests", {
      wedding_id: wedding.id,
      name:       name.trim(),
      phone:      phone.trim() || null,
      token,
    });
    if (g) {
      setGuests(prev => [...prev, g]);
      setName("");
      setPhone("");
    }
    setAdding(false);
  }

  async function deleteGuest(id) {
    const ok = await sbDelete(`guests?id=eq.${id}`);
    if (ok) setGuests(prev => prev.filter(g => g.id !== id));
  }

  function copyLink(token, key) {
    navigator.clipboard.writeText(guestUrl(token));
    setCopied(key);
    setTimeout(() => setCopied(""), 2000);
  }

  function copyAllWa() {
    const text = guests.map(g =>
      `${g.name}:\n${guestUrl(g.token)}`
    ).join("\n\n");
    navigator.clipboard.writeText(text);
    setCopied("all");
    setTimeout(() => setCopied(""), 2000);
  }

  return (
    <div className="ck-guests-box">
      <div className="ck-section-title">🔗 LINK PERSONAL TAMU</div>
      <div className="ck-guests-meta">
        Total tamu: <span>{guests.length}</span>
        {" · "}Setiap tamu mendapat link undangan dengan namanya sendiri
      </div>

      {/* Form tambah tamu */}
      <div className="ck-guest-form">
        <input
          className="ck-guest-inp"
          placeholder="Nama tamu *"
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === "Enter" && addGuest()}
          style={{ minWidth:160 }}
        />
        <input
          className="ck-guest-inp"
          placeholder="No. WhatsApp (opsional)"
          value={phone}
          onChange={e => setPhone(e.target.value)}
          style={{ maxWidth:180 }}
        />
        <button className="ck-guest-add" onClick={addGuest} disabled={!name.trim() || adding}>
          {adding ? "…" : "+ Tambah"}
        </button>
      </div>

      {/* Daftar tamu */}
      {loading
        ? <div className="ck-spin"/>
        : guests.length === 0
          ? <div className="ck-guest-empty">Belum ada tamu. Tambahkan nama tamu di atas.</div>
          : (
            <div className="ck-guest-list">
              {guests.map(g => (
                <div key={g.id} className="ck-guest-row">
                  <div className="ck-guest-name">{g.name}</div>
                  {g.phone && <div className="ck-guest-phone">{g.phone}</div>}
                  <div className="ck-guest-link-wrap">
                    <input className="ck-guest-link-url" readOnly
                      value={guestUrl(g.token)}
                      onFocus={e => e.target.select()}/>
                    <button className="ck-guest-link-copy"
                      onClick={() => copyLink(g.token, g.id)}>
                      {copied === g.id ? "✓" : "Salin"}
                    </button>
                  </div>
                  <button className="ck-guest-del"
                    onClick={() => deleteGuest(g.id)}>✕</button>
                </div>
              ))}
            </div>
          )
      }

      {/* Tombol copy semua */}
      {guests.length > 0 && (
        <div className="ck-copy-all">
          <button className="ck-btn-ghost" onClick={copyAllWa}>
            {copied === "all" ? "✓ Disalin!" : "📋 Salin Semua Link"}
          </button>
          {guests.some(g => g.phone) && (
            <button className="ck-btn-ghost" onClick={() => {
              // Buka WA blast — buka satu per satu untuk yang punya nomor
              guests.filter(g => g.phone).forEach((g, i) => {
                setTimeout(() => {
                  const ph = g.phone.replace(/^0/, "62").replace(/\D/g, "");
                  const msg = encodeURIComponent(
                    `Assalamu'alaikum ${g.name} 🎉\n\nKamu diundang ke pernikahan kami!\n\n` +
                    `✦ Link undangan pribadimu:\n${guestUrl(g.token)}\n\nSampai jumpa! 💍`
                  );
                  window.open(`https://wa.me/${ph}?text=${msg}`, "_blank");
                }, i * 600);
              });
            }}>
              💬 Kirim via WhatsApp
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ══════════════════════════════════════════════════════════════
export default function CekOrder() {
  const [query,     setQuery]     = useState("");
  const [loading,   setLoading]   = useState(false);
  const [notFound,  setNotFound]  = useState(false);
  const [order,     setOrder]     = useState(null);
  const [wedding,   setWedding]   = useState(null);   // ← baru
  const [copied,    setCopied]    = useState("");
  const [toast,     setToast]     = useState("");

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(""), 2500);
  }

  async function search() {
    if (!query.trim() || loading) return;
    setLoading(true);
    setNotFound(false);
    setOrder(null);
    setWedding(null);

    const q = query.trim().toUpperCase();
    const data = await sbGet(
      `orders?or=(order_number.eq.${q},contact_phone.eq.${q.toLowerCase()})&order=created_at.desc&limit=1`
    );

    if (!data) { setLoading(false); setNotFound(true); return; }
    setOrder(data);

    // Jika sudah active, load wedding record juga
    if (data.status === "active" && data.slug) {
      const w = await sbGet(`weddings?slug=eq.${data.slug}&limit=1`);
      setWedding(w || null);
    }

    setLoading(false);
  }

  function copyLink(text, key) {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(""), 2500);
  }

  const invitationUrl  = order?.slug ? `${SITE_URL}/undangan/${order.slug}` : null;
  const meta           = order ? (STATUS[order.status] || STATUS.pending_payment) : null;
  const addons         = order?.addons || [];
  const hasGaleri      = addons.includes("galeri_foto");
  const hasLinkPersonal= addons.includes("link_personal");
  const isActive       = order?.status === "active";

  return (
    <div style={{ background:"#0C0B09", minHeight:"100vh" }}>
      <style>{CSS}</style>

      <div className="ck-page">

        {/* ── SEARCH ── */}
        {!order && (
          <div className="ck-box">
            <div className="ck-tag">✦ CEK STATUS ✦</div>
            <h1 className="ck-title">Status Pesanan</h1>
            <p className="ck-sub">Masukkan nomor order atau nomor WhatsApp</p>
            <div className="ck-input-row">
              <input className="ck-inp"
                placeholder="MK1A2B3C atau 08xxxxxxxxxx"
                value={query}
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key==="Enter" && search()}
              />
              <button className="ck-btn" onClick={search} disabled={!query.trim()||loading}>
                {loading ? "…" : "Cek"}
              </button>
            </div>
            {loading   && <div className="ck-spin"/>}
            {notFound  && <p className="ck-err">Order tidak ditemukan. Coba cek kembali nomor ordermu.</p>}
          </div>
        )}

        {/* ── RESULT CARD ── */}
        {order && meta && (
          <div className="ck-card">

            {/* Top */}
            <div className="ck-card-top">
              <div>
                <div className="ck-order-id">{order.order_number}</div>
                <div className="ck-couple">
                  {order.groom_name}
                  <span style={{opacity:.18,margin:"0 8px",fontFamily:"serif"}}>&</span>
                  <span className="ck-bride">{order.bride_name}</span>
                </div>
              </div>
              <div className="ck-status-badge">
                <div className="ck-status-icon">{meta.icon}</div>
                <div className="ck-status-label" style={{color:meta.color}}>{meta.label}</div>
              </div>
            </div>

            {/* Details */}
            <div className="ck-details">
              {[
                ["Tema",      order.theme?.charAt(0).toUpperCase()+order.theme?.slice(1)||"—"],
                ["Paket",     order.bundle?.charAt(0).toUpperCase()+order.bundle?.slice(1)||"À la carte"],
                ["Tgl Nikah", order.wedding_date
                  ? new Date(order.wedding_date).toLocaleDateString("id-ID",{day:"numeric",month:"long",year:"numeric"})
                  : "—"],
                ["Kontak",    order.contact_phone||"—"],
              ].map(([k,v]) => (
                <div key={k}>
                  <div className="ck-det-key">{k}</div>
                  <div className="ck-det-val">{v}</div>
                </div>
              ))}
            </div>

            {/* Add-ons */}
            {addons.length > 0 && (
              <div className="ck-addons">
                <div className="ck-addons-title">ADD-ON AKTIF</div>
                <div className="ck-chips">
                  {addons.map(id => (
                    <span key={id} className="ck-chip">{ADDON_LABELS[id]||id}</span>
                  ))}
                </div>
              </div>
            )}

            {/* Status desc */}
            <div className="ck-status-info">
              <p className="ck-status-desc">{meta.desc}</p>
            </div>

            {/* Timeline */}
            <div className="ck-timeline">
              <div className="ck-tl-title">PROGRESS</div>
              {STEPS.map(s => {
                const state = getStepState(s.key, order.status);
                return (
                  <div key={s.key} className="ck-tl-row">
                    <div className={`ck-tl-dot ${state}`}/>
                    <span className={`ck-tl-label ${state}`}>{s.label}</span>
                  </div>
                );
              })}
            </div>

            {/* ── Link Undangan (saat active) ── */}
            {isActive && invitationUrl && (
              <div className="ck-link-box">
                <div className="ck-section-title">✦ LINK UNDANGANMU</div>
                <div className="ck-link-row">
                  <input className="ck-link-url" readOnly value={invitationUrl}
                    onFocus={e=>e.target.select()}/>
                  <button className="ck-link-copy" onClick={()=>copyLink(invitationUrl,"link")}>
                    {copied==="link"?"Disalin ✓":"Salin"}
                  </button>
                </div>
                <a href={invitationUrl} target="_blank" rel="noopener" className="ck-link-open">
                  Buka Undangan →
                </a>
              </div>
            )}

            {/* ── Galeri Foto (jika addon aktif + undangan active) ── */}
            {isActive && hasGaleri && wedding && (
              <GallerySection
                wedding={wedding}
                onSaved={gallery => {
                  setWedding(w => ({ ...w, content: { ...w.content, gallery } }));
                  showToast("✦ Foto tersimpan");
                }}
              />
            )}
            {isActive && hasGaleri && !wedding && (
              <div style={{padding:"16px 24px",borderBottom:"1px solid rgba(201,168,76,.07)",
                fontSize:".41rem",letterSpacing:".12em",color:"rgba(240,232,216,.28)"}}>
                🖼️ Galeri foto akan tersedia setelah undangan selesai dikonfigurasi admin.
              </div>
            )}

            {/* ── Link Personal — Manajemen Tamu ── */}
            {isActive && hasLinkPersonal && wedding && (
              <GuestsSection
                wedding={wedding}
                invitationUrl={invitationUrl}
              />
            )}
            {isActive && hasLinkPersonal && !wedding && (
              <div style={{padding:"16px 24px",borderBottom:"1px solid rgba(201,168,76,.07)",
                fontSize:".41rem",letterSpacing:".12em",color:"rgba(240,232,216,.28)"}}>
                🔗 Manajemen tamu akan tersedia setelah undangan selesai dikonfigurasi admin.
              </div>
            )}

            {/* Footer */}
            <div className="ck-card-foot">
              <div className="ck-foot-total">
                Total {order.total_price ? fmt(order.total_price) : "—"}
              </div>
              <button className="ck-new-search"
                onClick={() => { setOrder(null); setWedding(null); setQuery(""); setNotFound(false); }}>
                Cek Order Lain
              </button>
            </div>

          </div>
        )}

      </div>

      {toast && <div className="ck-toast">{toast}</div>}
    </div>
  );
}
